import { DropdownMenu, ListFilterIcon, IconButton } from '@buffer-mono/popcorn'

import type { MaskedFilterByChannelFragment } from '~publish/components/FilterByChannel'
import { useTagsQuery } from '~publish/hooks/useTags'
import { ChannelFilterSubMenu } from '~publish/pages/AllChannels/ChannelFilterSubMenu'
import { TagFilterSubMenu } from '~publish/pages/Channel/TagFilterSubMenu'

import type { JSX } from 'react'

export type InsightsFilterDropdownProps = {
  channels: MaskedFilterByChannelFragment
  selectedChannels: string[]
  onChannelSelect: (channelIds: string[]) => void
  selectedTags: string[]
  onTagSelect: (tagIds: string[]) => void
  /** Forwarded to the trigger so callers can toggle its visibility responsively. */
  className?: string
  /**
   * Channel IDs to render disabled (channels the actor can't view insights
   * for). Forwarded to {@link ChannelFilterSubMenu}.
   */
  disabledChannelIds?: Set<string>
}

/**
 * Collapses the All Channels Insights channel and tag filters into a single
 * `ListFilterIcon` dropdown, mirroring the All Channels Schedule page's
 * `QueueActions` pattern. Shown only when the header is too narrow to keep the
 * filters inline, so they don't wrap onto a new line.
 */
export function InsightsFilterDropdown({
  channels,
  selectedChannels,
  onChannelSelect,
  selectedTags,
  onTagSelect,
  className,
  disabledChannelIds,
}: InsightsFilterDropdownProps): JSX.Element {
  const { tags } = useTagsQuery()

  const showChannelFilter = channels.length > 0
  const showTagFilter = tags.length > 0

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger>
        <IconButton variant="tertiary" label="Filters" className={className}>
          <ListFilterIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content align="end">
        {showChannelFilter && (
          <ChannelFilterSubMenu
            channels={channels}
            selectedChannelIds={selectedChannels}
            onChannelSelect={onChannelSelect}
            disabledChannelIds={disabledChannelIds}
          />
        )}
        {showTagFilter && (
          <TagFilterSubMenu
            selectedTags={selectedTags}
            onTagSelect={onTagSelect}
          />
        )}
      </DropdownMenu.Content>
    </DropdownMenu>
  )
}
